import './ArrayField.scss'
import { useEffect, useRef, type ComponentProps, type ReactNode } from 'react'
import {
  MdOutlineAdd,
  MdOutlineArrowDownward,
  MdOutlineArrowUpward,
  MdOutlineDelete
} from 'react-icons/md'
import { Button } from '../Button'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import { tabbables } from '../layer'

export interface ArrayFieldProps extends Omit<ComponentProps<'fieldset'>, 'children'> {
  label: ReactNode
  /** One entry per row; `key` is useFieldArray's `field.id`. */
  items: readonly { key: string }[]
  renderItem: (index: number) => ReactNode
  onAdd: () => void
  onRemove: (index: number) => void
  addLabel?: string
  removeLabel?: (index: number) => string
  /** Shown when there are no rows. */
  emptyText?: ReactNode
  /** Show mode: no add/remove/move buttons, and the fieldset disables its controls. */
  isDisabled?: boolean
  onMoveUp?: (index: number) => void
  onMoveDown?: (index: number) => void
}

type PendingFocus = { kind: 'added' } | { kind: 'removed'; index: number } | null

/**
 * Repeated rows (react-hook-form's useFieldArray, kept presentational):
 * TemplateScreen parameters, Storecove VAT/CoC numbers, forwarded emails.
 * After Add, focus goes to the new row; after Remove, to the next row's
 * remove button, or Add when none is left.
 */
export function ArrayField({
  label,
  items,
  renderItem,
  onAdd,
  onRemove,
  addLabel = 'Add',
  removeLabel = (index) => `Remove item ${index + 1}`,
  emptyText,
  isDisabled = false,
  onMoveUp,
  onMoveDown,
  className,
  ...rest
}: ArrayFieldProps) {
  const rowsRef = useRef<HTMLOListElement>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  const pending = useRef<PendingFocus>(null)
  const hasMove = !isDisabled && Boolean(onMoveUp ?? onMoveDown)

  useEffect(() => {
    const intent = pending.current
    if (!intent) return
    pending.current = null
    const rows = rowsRef.current?.querySelectorAll<HTMLElement>(':scope > .array-field__row')
    if (intent.kind === 'added') {
      const last = rows?.[rows.length - 1]
      if (last) tabbables(last)[0]?.focus()
      return
    }
    const row = rows?.[Math.min(intent.index, (rows.length || 1) - 1)]
    const target = row?.querySelector<HTMLElement>('.array-field__remove') ?? addRef.current
    target?.focus()
  }, [items])

  return (
    <fieldset
      {...rest}
      disabled={isDisabled || rest.disabled}
      className={clx(
        'array-field',
        items.length === 0 && 'array-field--is-empty',
        hasMove && 'array-field--has-move',
        className
      )}
    >
      <legend className="array-field__legend">{label}</legend>
      {items.length === 0 ? (
        emptyText && <p className="array-field__empty">{emptyText}</p>
      ) : (
        <ol ref={rowsRef} className="array-field__rows">
          {items.map((item, index) => (
            <li key={item.key} className="array-field__row">
              <div className="array-field__content">{renderItem(index)}</div>
              {!isDisabled && (
                <div className="array-field__actions">
                  {onMoveUp && (
                    <IconButton
                      label={`Move item ${index + 1} up`}
                      icon={<MdOutlineArrowUpward />}
                      disabled={index === 0}
                      onClick={() => {
                        onMoveUp(index)
                      }}
                    />
                  )}
                  {onMoveDown && (
                    <IconButton
                      label={`Move item ${index + 1} down`}
                      icon={<MdOutlineArrowDownward />}
                      disabled={index === items.length - 1}
                      onClick={() => {
                        onMoveDown(index)
                      }}
                    />
                  )}
                  <IconButton
                    className="array-field__remove"
                    label={removeLabel(index)}
                    icon={<MdOutlineDelete />}
                    onClick={() => {
                      pending.current = { kind: 'removed', index }
                      onRemove(index)
                    }}
                  />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {!isDisabled && (
        <div className="array-field__footer">
          <Button
            ref={addRef}
            size="sm"
            icon={<MdOutlineAdd />}
            onClick={() => {
              pending.current = { kind: 'added' }
              onAdd()
            }}
          >
            {addLabel}
          </Button>
        </div>
      )}
    </fieldset>
  )
}
